import { useEffect } from 'react';
import { observable, runInAction, makeObservable } from 'mobx';
import { transformHelper } from './transformHelper';
import Bugsnag from '@vromo/analytics/bugsnag';

class NetworkStore {
    sockets: { [key: string]: WebSocketClient } = {};

    constructor() {
        makeObservable(this, {
            sockets: observable
        });
    }
}

const networkStore = new NetworkStore();

const MIN_RECONNECT_INTERVAL_MS = 500;
const MAX_RECONNECT_INTERVAL_MS = 6400;

export class WebSocketClient {
    autoReconnectInterval = MIN_RECONNECT_INTERVAL_MS;

    shouldReconnect = true;

    instance: WebSocket | null = null;
    url = '';

    event: NodeJS.Timer | number = -1;
    lastPong = 0;

    transforms: Array<(payloadType: number, packet: any) => any> = [];

    constructor() {
        if (/Chrome/.test(navigator.userAgent)) {
            this.event = setInterval(() => {
                const { instance } = this;
                if(!instance) return;

                if (instance.readyState === instance.OPEN) {
                    instance.send('[-1, "ping"]');
                }

                if (this.lastPong !== 0 && this.lastPong < Date.now() - 20 * 1000) {
                    this.shouldReconnect = true;
                    instance.close();
                }
            }, 15000);
        }
    }

    onmessage = (payloadType: number, packet: any): void => {
        Bugsnag.leaveBreadcrumb('Websocket event received', {
            packet: JSON.stringify(packet)
        });

        this.transforms.forEach((transform) => transform(payloadType, packet));
    };

    onerror = (e: Event): void => {
        console.error(e);
    };

    open = (url: string): void => {
        this.lastPong = 0;
        this.url = url;
        this.instance = new WebSocket(this.url);

        this.instance.onopen = () => {
            this.autoReconnectInterval = MIN_RECONNECT_INTERVAL_MS;
        }

        this.instance.onmessage = ({ data }) => {
            const [id, payloadType, packet] = JSON.parse(data);

            this.lastPong = Date.now();

            switch (id) {
                case 0:
                    this.onmessage(payloadType, packet);
                    break;
            }
        };

        this.instance.onclose = (e) => {
            if (this.shouldReconnect) {
                this.reconnect();
            }
        };

        this.instance.onerror = (e) => {
            // @ts-expect-error websocket types need investigating
            switch (e.code) {
                case 'ECONNREFUSED':
                    this.reconnect();
                    break;
                default:
                    this.onerror(e);
                    break;
            }
        };
    };

    reconnect = (): void => {
        setTimeout(() => {
            this.open(this.url);
        }, this.autoReconnectInterval);

        this.autoReconnectInterval = Math.min(
            2 * this.autoReconnectInterval,
            MAX_RECONNECT_INTERVAL_MS
        );
    };

    destroy = (): void => {
        this.shouldReconnect = false;
        this.instance?.close();
        clearInterval(this.event);
    };
}

export function useSocket(
    path: string,
    store: any,
    onMessage: (() => void) | null,
    transform?: (it: any) => any
): void {
    useEffect(() => {
        return subscribe(path, store, onMessage, transform);
    }, [path, store]);
}

export function subscribe(
    url: string,
    store: any,
    onMessage: (() => void) | null,
    transform = (it: any) => it
): () => void {
    const sockets = networkStore.sockets;
    let socket;

    if (sockets[url]) {
        socket = sockets[url];
    } else {
        socket = new WebSocketClient();

        socket.open(url);

        sockets[url] = socket;
    }

    socket!.transforms.push((payloadType, packet) => {
        if (payloadType === 0) {
            runInAction(() => {
                const transformed = transform(packet);

                if (transformed != undefined) {
                    transformHelper(transformed, store);
                }
                onMessage && onMessage();
            });
        }
    });

    return () => unsubscribe(url);
}

export function unsubscribe(url: string): void {
    const sockets = networkStore.sockets;

    if (!sockets[url]) return;
    
    sockets[url]?.destroy();
    delete sockets[url];
}
